import type { CSSProperties, JSX, ReactNode } from 'react';
import type { ViewerTier } from './roles';
import type { AdminSection } from './types';

/**
 * The standard Integrations body — an external connection's status + the portal's
 * own Connect/Disconnect/Sync actions (injected, because every endpoint differs).
 * A pure shell: @cca/ui owns none of the OAuth/plumbing. Disconnect etc. should be
 * a <ConfirmAction> supplied by the portal (irreversible).
 */
export interface IntegrationSectionProps {
  name: string;
  connected: boolean;
  statusLine?: ReactNode;
  actions?: ReactNode;
  error?: string;
  icon?: ReactNode;
}

const muted: CSSProperties = { fontSize: 13, color: 'var(--color-muted, #6b7280)', marginTop: 6 };

export function IntegrationSection({
  name,
  connected,
  statusLine,
  actions,
  error,
  icon,
}: IntegrationSectionProps): JSX.Element {
  const pill: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    fontWeight: 700,
    padding: '3px 10px',
    borderRadius: 'var(--radius-pill, 999px)',
    color: connected ? '#166534' : '#8a5a00',
    background: connected ? 'rgba(22,101,52,0.12)' : 'rgba(180,83,9,0.12)',
  };
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {icon}
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-ink, #111827)' }}>
          {name}
        </span>
        <span style={pill}>{connected ? 'Connected' : 'Not connected'}</span>
      </div>
      {statusLine && <div style={muted}>{statusLine}</div>}
      {error && <p style={{ ...muted, color: 'var(--color-danger, #b3261e)' }}>{error}</p>}
      {actions && (
        <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>{actions}</div>
      )}
    </div>
  );
}

/** Convenience factory → an AdminSection (Integrations, `superuser`-tier by default). */
export function integrationSection(
  opts: IntegrationSectionProps & {
    key?: string;
    title?: string;
    minRole?: ViewerTier;
  },
): AdminSection {
  const { key, title, minRole, ...body } = opts;
  return {
    key: key ?? `integration-${opts.name.toLowerCase()}`,
    title: title ?? 'Integrations',
    minRole: minRole ?? 'superuser',
    body: <IntegrationSection {...body} />,
  };
}
